import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

const ScrollToTop = lazy(() => import("./ScrollToTop"));
const Layout = lazy(() => import("../Layouts/Layout"));
const Login = lazy(() => import("../login/Login"));

// Admin Panel Components
const AdminLayout = lazy(() => import("../Layouts/AdminLayout"));
const Dashboard = lazy(() => import("../adminPanel/dashboard/Dashboard"));
const BlogListingAdmin = lazy(() => import("../adminPanel/blogs/BlogListingAdmin"));
const AddBlogAdmin = lazy(() => import("../adminPanel/blogs/AddBlogAdmin"));
const EditBlogAdmin = lazy(() => import("../adminPanel/blogs/EditBlogAdmin"));
const AIBlogs = lazy(() => import("../adminPanel/aiBlogs/AIBlogs"));
const AddAIBlogs = lazy(() => import("../adminPanel/aiBlogs/AddAIBlogs"));
const EditAIBlogs = lazy(() => import("../adminPanel/aiBlogs/EditAIBlogs"));
const AuthorListing = lazy(() => import("../adminPanel/author/AuthorListing"));
const AddAuthor = lazy(() => import("../adminPanel/author/AddAuthor"));
const EditAuthor = lazy(() => import("../adminPanel/author/EditAuthor"));
const Tags = lazy(() => import("../adminPanel/tags/Tags"));
const AddTags = lazy(() => import("../adminPanel/tags/AddTags"));
const EditTags = lazy(() => import("../adminPanel/tags/EditTags"));
const Category = lazy(() => import("../adminPanel/category/Category"));
const AddCategory = lazy(() => import("../adminPanel/category/AddCategory"));
const EditCategory = lazy(() => import("../adminPanel/category/EditCategory"));
const SeoListing = lazy(() => import("../adminPanel/seo/SeoListing"));
const SeoCreate = lazy(() => import("../adminPanel/seo/SeoCreate"));
const SeoEdit = lazy(() => import("../adminPanel/seo/SeoEdit"));
const CaseStudyListAdmin = lazy(() => import("../adminPanel/caseStudy/CaseStudyListAdmin"));
const AddCaseStudy = lazy(() => import("../adminPanel/caseStudy/AddCaseStudy"));
const EditCaseStudy = lazy(() => import("../adminPanel/caseStudy/EditCaseStudy"));
const CaseCategoryList = lazy(() => import("../adminPanel/caseCategory/CaseCategoryList"));
const AddCaseCategory = lazy(() => import("../adminPanel/caseCategory/AddCaseCategory"));
const EditCaseCategory = lazy(() => import("../adminPanel/caseCategory/EditCaseCategory"));
const NewsLetter = lazy(() => import("../adminPanel/newsLetter/NewsLetter"));
const ContactListingAdmin = lazy(() => import("../adminPanel/contact/contactListingAdmin"));
const FeaturePageListing = lazy(() => import("../adminPanel/webPages/FeaturePageListing"));
const FeaturePageCreate = lazy(() => import("../adminPanel/webPages/FeaturePageCreate"));
const FeaturePageEdit = lazy(() => import("../adminPanel/webPages/FeaturePageEdit"));
const FeatureEdit = lazy(() => import("../adminPanel/feature/FeatureEdit"));
const FeatureCreate = lazy(() => import("../adminPanel/feature/FeatureCreate"));
const FeatureListing = lazy(() => import("../adminPanel/feature/FeatureListing"));
const AdminMedia = lazy(() => import("../adminPanel/media/AdminMedia"));
const AddMedia = lazy(() => import("../adminPanel/media/AddMedia"));
const EditMedia = lazy(() => import("../adminPanel/media/EditMedia"));
const CloneApps = lazy(() => import("../adminPanel/cloneApps/CloneApps"));
const AddCloneApps = lazy(() => import("../adminPanel/cloneApps/AddCloneApps"));
const EditCloneApps = lazy(() => import("../adminPanel/cloneApps/EditCloneApps"));
const AddTestimonial = lazy(() => import("../adminPanel/testimonial/AddTestimonial"));
const TestimonialList = lazy(() => import("../adminPanel/testimonial/TestimonialList"));
const EditTestimonial = lazy(() => import("../adminPanel/testimonial/EditTestimonial"));
const EmailDynamic = lazy(() => import("../adminPanel/emailDynamic/EmailDynamic"));
const AddEmails = lazy(() => import("../adminPanel/emailDynamic/AddEmails"));
const EditEmails = lazy(() => import("../adminPanel/emailDynamic/EditEmails"));

// User Layout Components
const Home = lazy(() => import("../Home/Home"));
const About = lazy(() => import("../About/About"));
const Contact = lazy(() => import("../Contact/Contact"));
const ComingSoon = lazy(() => import("../comingSoon/ComingSoon"));
const ThankYou = lazy(() => import("../thankyou/ThankYou"));
const PrivacyPolicy = lazy(() => import("../privacy/PrivacyPolicy"));
const PageNotFound = lazy(() => import("../404page/PageNotFound"));
const Pricing = lazy(() => import("../pricing/Pricing"));
const MediaPage = lazy(() => import("../Media_Page/MediaPage"));

// Clone Apps
const CloneAppsDynamic = lazy(() => import("../cloneApps/UberClone/CloneAppsDynamic"));
const LyftClone = lazy(() => import("../cloneApps/LyftClone/LyftClone"));
const ZipcarClone = lazy(() => import("../cloneApps/ZipcarClone/ZipcarClone"));
const CabifyClone = lazy(() => import("../cloneApps/cabifyClone/CabifyClone"));
const BoltClone = lazy(() => import("../cloneApps/boltClone/BoltClone"));
const XanhClone = lazy(() => import("../cloneApps/xanhClone/XanhClone"));
const MaiLinhClone = lazy(() => import("../cloneApps/maiLinhClone/MaiLinhClone"));
const ZoomZoom = lazy(() => import("../cloneApps/zoomZoom/ZoomZoom"));
const YellowCab = lazy(() => import("../cloneApps/yellowCab/YellowCab"));
const URideClone = lazy(() => import("../cloneApps/uRideClone/URideClone"));
const Taxis99 = lazy(() => import("../cloneApps/99taxis/Taxis99"));
const UberClone = lazy(() => import("../cloneApps/UberClone/UberClone"));
const EasyTaxi = lazy(() => import("../cloneApps/easyTaxi/EasyTaxi"));
const VadeTaxi = lazy(() => import("../cloneApps/vadeTaxi/VadeTaxi"));
const FreeNow = lazy(() => import("../cloneApps/freeNow/FreeNow"));
const BlaBlaClone = lazy(() => import("../cloneApps/blabla/BlaBlaClone"));
const BlaBlaItaly = lazy(() => import("../cloneApps/blablaItaly/BlaBlaItaly"));
const GrabClone = lazy(() => import("../cloneApps/grabClone/GrabClone"));

// Industries
const Restaurant = lazy(() => import("../restaurant/Restaurant"));
const Telemedicine = lazy(() => import("../telemedicine/Telemedicine"));
const Education = lazy(() => import("../education/Education"));
const FoodAndGrocery = lazy(() => import("../foodAndGrocery/FoodAndGrocery"));
const HealthCare = lazy(() => import("../healthcare/HealthCare"));
const Industries = lazy(() => import("../industries/Industries"));
const Pharmacy = lazy(() => import("../pharmacy/Pharmacy"));
const Ecommerce = lazy(() => import("../ecommerce/Ecommerce"));

// Solutions
const ShuttleSoftware = lazy(() => import("../shuttle_software/ShuttleSoftware"));
const CarRental = lazy(() => import("../carRental/CarRental"));
const CarPooling = lazy(() => import("../carPooling/CarPooling"));
const WhiteLabelSolutions = lazy(() => import("../WhiteLabelSolutions/WhiteLabel"));
const BikeRental = lazy(() => import("../bikeRental/BikeRental"));
const TaxiDispatchSoftware = lazy(() => import("../taxiDispatchSoftware/taxiDispatchSoftware"));
const CustomAppDevelopment = lazy(() => import("../customAppDevelopment/CustomAppDevelopment"));
const WebAppDevelopment = lazy(() => import("../webAppDevelopment/WebAppDevelopment"));
const TaxiSoftware = lazy(() => import("../TaxiSoftware/TaxiSoftware"));

// blogs front end
const BlogListing = lazy(() => import("../Blogs/BlogListing"));
const BlogDetails = lazy(() => import("../BlogDetails/BlogDetails"));
const AIBlogDetail = lazy(() => import("../AIblogDetail/AIBlogDetail"));

// Case Study front end
const CaseStudyListing = lazy(() => import("../caseStudy/CaseStudyListing"));
const CaseStudyDetailNew = lazy(() => import("../caseStudyDetails/CaseStudyDetailNew"));

// features front end
const Features = lazy(() => import("../Features/Features"));
const FeatureNew = lazy(() => import("../featureNew/FeatureNew"));


export const NavRouter = () => {
  return (
    <>
      <Suspense fallback={
        <>
          {/* <div className="loader-screen">
          <img loading="lazy" src="/assets/home/loading-logo.webp" alt="logo" className="img-responsive" />
        </div> */}
          <div className="loader-only-css">
            <div className="loader-inner-css">
              <div></div>
            </div>
          </div>
        </>
      }>
        <ScrollToTop />
        <Routes>

          {/*=============== Admin Layout Start ===============*/}
          <Route path="Login" element={<Login />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />

            <Route path="blog" element={<BlogListingAdmin />} />
            <Route path="add-blog" element={<AddBlogAdmin />} />
            <Route path="edit-blog/:blog_id" element={<EditBlogAdmin />} />

            <Route path="ai-blog" element={<AIBlogs />} />
            <Route path="add-ai-blog" element={<AddAIBlogs />} />
            <Route path="edit-ai-blog/:aiblog_id" element={<EditAIBlogs />} />

            <Route path="author" element={<AuthorListing />} />
            <Route path="add-author" element={<AddAuthor />} />
            <Route path="edit-author/:author_id" element={<EditAuthor />} />

            <Route path="tags" element={<Tags />} />
            <Route path="add-tags" element={<AddTags />} />
            <Route path="edit-tags/:tags_id" element={<EditTags />} />

            <Route path="category" element={<Category />} />
            <Route path="add-category" element={<AddCategory />} />
            <Route path="edit-category/:category_id" element={<EditCategory />} />

            <Route path="seo" element={<SeoListing />} />
            <Route path="add-seo" element={<SeoCreate />} />
            <Route path="edit-seo/:seo_id" element={<SeoEdit />} />

            <Route path="feature-pages" element={<FeaturePageListing />} />
            <Route path="add-feature-page" element={<FeaturePageCreate />} />
            <Route path="edit-feature-page/:featurepage_id" element={<FeaturePageEdit />} />

            <Route path="features" element={<FeatureListing />} />
            <Route path="add-feature" element={<FeatureCreate />} />
            <Route path="edit-feature/:featurepage_id" element={<FeatureEdit />} />

            <Route path="case-study" element={<CaseStudyListAdmin />} />
            <Route path="add-case-study" element={<AddCaseStudy />} />
            <Route path="edit-case-study/:casestudy_id" element={<EditCaseStudy />} />

            <Route path="media" element={<AdminMedia />} />
            <Route path="add-media" element={<AddMedia />} />
            <Route path="edit-media/:media_id" element={<EditMedia />} />

            <Route path="case-category" element={<CaseCategoryList />} />
            <Route path="add-case-category" element={<AddCaseCategory />} />
            <Route path="edit-case-category/:casecategory_id" element={<EditCaseCategory />} />

            <Route path="clone-app" element={<CloneApps />} />
            <Route path="add-clone-app" element={<AddCloneApps />} />
            <Route path="edit-clone-app/:cloneapp_url" element={<EditCloneApps />} />

            <Route path="testimonial" element={<TestimonialList />} />
            <Route path="add-testimonial" element={<AddTestimonial />} />
            <Route path="edit-testimonial/:testimonial_id" element={<EditTestimonial />} />

            <Route path="emails" element={<EmailDynamic />} />
            <Route path="add-emails" element={<AddEmails />} />
            <Route path="edit-emails/:email_id" element={<EditEmails />} />

            <Route path="contact" element={<ContactListingAdmin />} />
            <Route path="newsletter" element={<NewsLetter />} />
          </Route>
          {/*=============== Admin Layout Finish ===============*/}


          {/*================ User Layout Routes ================*/}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />

            <Route path="about" element={<About />} />
            <Route path="contact" element={<Contact />} />
            <Route path="thankyou" element={<ThankYou />} />
            <Route path="404" element={<PageNotFound />} />
            <Route path="*" element={<Navigate to={"/404"} />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="media" element={<MediaPage />} />
            <Route path="coming-soon" element={<ComingSoon />} />


            {/* Blog Front End Routes */}
            <Route path="blog" element={<BlogListing />} />
            <Route path="blog/:blog_slug" element={<BlogDetails />} />
            <Route path="ai-blog/:aiblog_slug" element={<AIBlogDetail />} />


            {/* Case Study front end Routes */}
            <Route path="case-study" element={<CaseStudyListing />} />
            <Route path="case-study/:casestudy_slug" element={<CaseStudyDetailNew />} />
            {/* <Route path="case-study" element={<CaseStudy />} /> */}
            {/* <Route path="case-study-detail" element={<CaseStudyDetail />} /> */}


            {/* Feature front end Routes */}
            <Route path="features/:featurepageId" element={<Features />} />
            <Route path="features/taxi-software" element={<FeatureNew />} />
            {/* <Route path="features" element={<Features />} /> */}


            {/* All Solution Routes */}
            <Route path="white-lable-carpool-app-dispatch-software" element={<CarPooling />} />
            <Route path="best-taxi-dispatch-software" element={<TaxiSoftware />} />
            <Route path="shuttle-software" element={<ShuttleSoftware />} />
            <Route path="car-rental-software" element={<CarRental />} />
            <Route path="white-label-solutions" element={<WhiteLabelSolutions />} />
            <Route path="bike-rental" element={<BikeRental />} />
            <Route path="airport-transfer-software" element={<TaxiDispatchSoftware />} />
            <Route path="custom-app-development" element={<CustomAppDevelopment />} />
            <Route path="web-app-development" element={<WebAppDevelopment />} />


            {/*All Industries Routes */}
            <Route path="best-logistic-app-development-company" element={<Industries />} />
            <Route path="custom-healthcare-app-development-services" element={<HealthCare />} />
            <Route path="food-grocery-delivery-app-development-company" element={<FoodAndGrocery />} />
            <Route path="best-pharmacy-app-development-company" element={<Pharmacy />} />
            <Route path="ecommerce-mobile-app-development-company" element={<Ecommerce />} />
            <Route path="education-app-development-services" element={<Education />} />
            <Route path="telemedicine-app-development-company" element={<Telemedicine />} />
            <Route path="restaurant-app-development-company" element={<Restaurant />} />


            {/* All Clone App Routes*/}
            <Route path="clone-app/:cloneapp_url" element={<CloneAppsDynamic />} />
            <Route path="clone-app/lyft-clone-app" element={<LyftClone />} />
            <Route path="clone-app/zipcar-clone-app" element={<ZipcarClone />} />
            <Route path="clone-app/cabify-clone-app" element={<CabifyClone />} />
            <Route path="clone-app/bolt-clone-app" element={<BoltClone />} />
            <Route path="clone-app/mai-linh-clone-app" element={<MaiLinhClone />} />
            <Route path="clone-app/xanh-sm-clone-app" element={<XanhClone />} />
            <Route path="clone-app/zoom-zoom-clone-app" element={<ZoomZoom />} />
            <Route path="clone-app/yellow-cab-clone-app" element={<YellowCab />} />
            <Route path="clone-app/uride-clone-app" element={<URideClone /> } />
            <Route path="clone-app/easy-taxi-clone-app" element={<EasyTaxi /> } />
            <Route path="clone-app/va-de-taxi-clone-app" element={<VadeTaxi /> } />
            <Route path="clone-app/free-now-clone-app" element={<FreeNow /> } />
            <Route path="clone-app/99taxis-clone-app" element={<Taxis99 /> } />
            <Route path="clone-app/uber-clone-app" element={<UberClone />} />
            <Route path="clone-app/blabla-car-clone-app" element={<BlaBlaClone />} />
            <Route path="clone-app/blabla-car-clone-app-italy" element={<BlaBlaItaly />} />
            <Route path="clone-app/grab-clone-app" element={<GrabClone />} />

            {/* Redirections */}
            <Route path="/car-pooling" element={<Navigate to="/white-lable-carpool-app-dispatch-software" replace />} />
            <Route path="/taxi-software" element={<Navigate to="/best-taxi-dispatch-software" replace />} />
            <Route path="/car-rental" element={<Navigate to="/car-rental-software" replace />} />
            <Route path="industries" element={<Navigate to={"/best-logistic-app-development-company"} replace />} />
            <Route path="healthcare" element={<Navigate to={"/custom-healthcare-app-development-services"} replace />} />
            <Route path="food-and-grocery-app" element={<Navigate to={"/food-grocery-delivery-app-development-company"} replace />} />
            <Route path="pharmacy-app" element={<Navigate to={"/best-pharmacy-app-development-company"} replace />} />
            <Route path="ecommerce-app" element={<Navigate to={"/ecommerce-mobile-app-development-company"} replace />} />
            <Route path="lyft-clone-app" element={<Navigate to={"/clone-app/lyft-clone-app"} replace />} />
            <Route path="uber-clone-app" element={<Navigate to={"/clone-app/uber-clone-app"} replace />} />
            <Route path="/best-taxi-dispatc" element={<Navigate to="/best-taxi-dispatch-software" replace />} />
            <Route path="/healthcare" element={<Navigate to="/custom-healthcare-app-development-services" replace />} />
            <Route path="/industries" element={<Navigate to="/best-logistic-app-development-company" replace />} />
            <Route path="/ecommerce-app" element={<Navigate to="/ecommerce-mobile-app-development-company" replace />} />
            <Route path="/blog-detail/undefined" element={<Navigate to="/blog" replace />} />
            <Route path="/casestudy/ontap-case-study" element={<Navigate to="/case-study/ontap-case-study" replace />} />
            <Route path="/casestudy/togopool-case-study" element={<Navigate to="/case-study/togopool-case-study" replace />} />
            <Route path="/best-taxi-dispatch-software/best-taxi-dispatch-software" element={<Navigate to="/best-taxi-dispatch-software" replace />} />
            <Route path="/blog/how-to-choose-the-right-taxi-dispatch-software-for-your-growing-busin" element={<Navigate to="/blog/how-to-choose-the-right-taxi-dispatch-software-for-your-growing-business" replace />} />
            <Route path="/blog/key-considerations-for-choosing-taxi-management-software-for-g" element={<Navigate to="/blog/key-considerations-for-choosing-taxi-management-software-for-growing-fleets" replace />} />
            <Route path="/blog/what-is-taxi-dispatch-software-and-how-it-can-transform-yo" element={<Navigate to="/blog/what-is-taxi-dispatch-software-and-how-it-can-transform-your-taxi-business" replace />} />
            <Route path="blog/a-comprehensive-guide-to-taxi-dispatch-system-price" element={<Navigate to="/blog/a-comprehensive-guide-to-taxi-dispatch-system-prices-and-roi" replace />} />
            <Route path="/blog/a-comprehensive-guide-to-custom-app-vs-white-label-for-ta" element={<Navigate to="/blog/a-comprehensive-guide-to-custom-app-vs-white-label-for-taxi-companies" replace />} />
            <Route path="/features/66f7a4b7770fe7ade1f72f93" element={<Navigate to="/features/taxi-dispatch-software" replace />} />
            <Route path="/features/66f7a494770fe7ade1f72f84" element={<Navigate to="/features/taxi-dispatch-software" replace />} />
            <Route path="/features/66f7a486770fe7ade1f72f80" element={<Navigate to="/features/taxi-dispatch-software" replace />} />
            <Route path="/features/66f7a4ab770fe7ade1f72f8e" element={<Navigate to="/features/taxi-dispatch-software" replace />} />
            <Route path="/features/66f7a49f770fe7ade1f72f89" element={<Navigate to="/features/taxi-dispatch-software" replace />} />
            <Route path="/features/66f295c630826ed7475eee6a" element={<Navigate to="/features/taxi-dispatch-software" replace />} />
            <Route path="/features/66f3ba421e2135e008de309d" element={<Navigate to="/features/taxi-dispatch-software" replace />} />
            <Route path="/blog/create-lyft-and-uber-alternative-app-to-start-your-own-taxibusiness" element={<Navigate to="/blog/create-lyft-and-uber-alternative-app-to-start-your-own-taxi-business" replace />} />
            <Route path="/carpool-software" element={<Navigate to="/white-lable-carpool-app-dispatch-software" replace />} />
            <Route path="/blog/the-rise-of-white-label-taxi-apps:-what-you-need-to-know" element={<Navigate to="/blog/the-rise-of-white-label-taxi-apps-what-you-need-to-know" replace />} />
            <Route path="/bike-renta" element={<Navigate to="/bike-rental" replace />} />
            <Route path="/blog/top-10-taxi-dispatch-software-providers" element={<Navigate to="/blog/top-taxi-dispatch-software-provider-features-pricing-and-reviews" replace />} />

          </Route>
        </Routes>
      </Suspense>
    </>
  );
};
